import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, FileCheck2, X, Lock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEntitlements } from "@/context/EntitlementContext";
import { toast } from "sonner";

const INPUT = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text";
const money = (c, v) => `${c || ""} ${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const badge = (s) => s === "issued"
  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
  : "bg-slate-100 text-slate-500 dark:bg-ink-panel dark:text-slate-400";

export default function Invoices() {
  const { hasPermission } = useAuth();
  const { hasFeature } = useEntitlements();
  const navigate = useNavigate();
  const canFullInvoice = hasFeature("full_invoice_receipt");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    try { setRows((await api.get("/finance/invoice")).data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Billing</p>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">Invoices</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Invoices anchor to the PI. Balances reflect issued receipts against the total.</p>
        </div>
        {hasPermission("invoices.create") && (canFullInvoice ? (
          <button onClick={() => setCreating(true)} data-testid="invoice-new-button"
            className="inline-flex items-center gap-2 rounded-lg bg-ink-gold px-4 py-2.5 text-sm font-semibold text-[#0B1120] hover:brightness-110">
            <Plus className="h-4 w-4" /> New Invoice
          </button>
        ) : (
          <button onClick={() => navigate("/upgrade?feature=full_invoice_receipt")} data-testid="invoice-new-locked"
            className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-700 hover:bg-amber-100 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
            <Lock className="h-4 w-4" /> Invoices · Growth plan
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600 dark:text-ink-gold" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center dark:border-ink-line" data-testid="invoices-empty-state">
          <FileCheck2 className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">No invoices yet</p>
          <p className="mt-1 text-xs text-slate-400">Create an invoice from a Pro Forma Invoice once its Booking exists.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-ink-line dark:bg-ink-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 dark:border-ink-line dark:bg-ink/40">
                  <th className="px-5 py-3 font-medium">Invoice #</th>
                  <th className="px-5 py-3 font-medium">Customer</th>
                  <th className="px-5 py-3 font-medium">Booking #</th>
                  <th className="px-5 py-3 font-medium">Total</th>
                  <th className="px-5 py-3 font-medium">Paid</th>
                  <th className="px-5 py-3 font-medium">Remaining</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => setDetailId(r.id)} className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50 dark:border-ink-line dark:hover:bg-ink-panel/40" data-testid={`invoice-row-${r.id}`}>
                    <td className="px-5 py-3 font-mono-tf text-xs font-semibold text-slate-700 dark:text-ink-gold">{r.document_number || "— draft —"}</td>
                    <td className="px-5 py-3 font-medium text-slate-800 dark:text-ink-text">{r.customer_name || "—"}</td>
                    <td className="px-5 py-3 font-mono-tf text-xs text-slate-600 dark:text-slate-300">{r.booking_number || "—"}</td>
                    <td className="px-5 py-3 font-mono-tf text-slate-700 dark:text-slate-200">{money(r.currency, r.total_amount)}</td>
                    <td className="px-5 py-3 font-mono-tf text-slate-700 dark:text-slate-200">{money(r.currency, r.amount_paid)}</td>
                    <td className="px-5 py-3 font-mono-tf text-slate-700 dark:text-slate-200">{money(r.currency, r.remaining_balance)}</td>
                    <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge(r.status)}`}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating && <CreateInvoiceModal onClose={() => setCreating(false)} onDone={async (id) => { setCreating(false); await load(); setDetailId(id); }} />}
      {detailId && <InvoiceDetail id={detailId} onClose={() => setDetailId(null)} onChange={load} />}
    </div>
  );
}

function CreateInvoiceModal({ onClose, onDone }) {
  const [pis, setPis] = useState([]);
  const [pid, setPid] = useState("");
  const [due, setDue] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const all = (await api.get("/finance/proforma_invoice", { params: { status: "issued" } })).data;
        setPis(all.filter((p) => p.payment_verified));
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, []);
  const submit = async () => {
    if (!pid) { toast.error("Select a Pro Forma Invoice"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/finance/invoice", { proforma_id: pid, settlement_due_date: due || undefined });
      toast.success("Invoice draft created");
      await onDone(data.id);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="invoice-create-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900 dark:text-ink-text">New Invoice</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Verified Pro Forma Invoice</label>
            <select value={pid} onChange={(e) => setPid(e.target.value)} data-testid="invoice-pi-select" className={INPUT}>
              <option value="">Select a PI…</option>
              {pis.map((p) => <option key={p.id} value={p.id}>{p.document_number} — {p.customer_name} — {money(p.currency, p.total_amount)} ({p.payment_terms})</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Settlement due date (optional)</label>
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="invoice-due-input" className={INPUT} />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 dark:border-ink-line dark:text-slate-300">Cancel</button>
          <button onClick={submit} disabled={saving || !pid} data-testid="invoice-create-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-ink-gold px-4 py-2 text-sm font-semibold text-[#0B1120] disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create Draft
          </button>
        </div>
      </div>
    </div>
  );
}

function InvoiceDetail({ id, onClose, onChange }) {
  const { hasPermission } = useAuth();
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [settle, setSettle] = useState(false);

  const load = async () => {
    try { setInv((await api.get(`/finance/invoice/${id}`)).data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const issue = async () => {
    setBusy(true);
    try { await api.post(`/finance/invoice/${id}/issue`, {}); toast.success("Invoice issued"); await load(); await onChange(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!inv) return null;
  const rel = inv.related || {};
  const prepaid = rel.proforma_invoice?.payment_terms === "100_prepaid";
  const remaining = Number(inv.remaining_balance || 0);
  const R = ({ label, children, testid }) => (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-2 last:border-0 dark:border-ink-line">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-right font-medium text-slate-800 dark:text-ink-text" data-testid={testid}>{children}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="invoice-detail-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Invoice</p>
            <h2 className="font-display text-xl font-bold text-slate-900 dark:text-ink-text" data-testid="invoice-detail-number">{inv.document_number || "— Draft —"}</h2>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>
        <div className="text-sm">
          <R label="Customer">{inv.customer_name || "—"}</R>
          <R label="Status">{inv.status}</R>
          <R label="Pro Forma Invoice">{rel.proforma_invoice?.document_number || "—"}</R>
          <R label="Booking #">{inv.booking_number || "—"}</R>
          <R label="Payment terms">{rel.proforma_invoice?.payment_terms || "—"}</R>
          <R label="Total">{money(inv.currency, inv.total_amount)}</R>
          <R label="Amount paid" testid="invoice-paid">{money(inv.currency, inv.amount_paid)}</R>
          <R label="Remaining balance" testid="invoice-remaining">{money(inv.currency, inv.remaining_balance)}</R>
          <R label="Settlement due">{inv.settlement_due_date || "—"}</R>
          <R label="Deposit receipts">{(rel.deposit_receipts || []).map((r) => r.document_number || "draft").join(", ") || "—"}</R>
          <R label="Settlement receipts">{(rel.settlement_receipts || []).map((r) => r.document_number || "draft").join(", ") || "—"}</R>
        </div>

        {prepaid ? (
          <p className="mt-4 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300" data-testid="invoice-prepaid-note">
            Fully prepaid (100%) — no settlement stage required.
          </p>
        ) : null}

        <div className="mt-5 flex flex-wrap gap-2">
          {inv.status === "draft" && hasPermission("invoices.issue") && (
            <button onClick={issue} disabled={busy} data-testid="invoice-issue-button"
              className="inline-flex items-center gap-2 rounded-lg bg-ink-gold px-4 py-2 text-sm font-semibold text-[#0B1120] disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Issue Invoice
            </button>
          )}
          {inv.status === "issued" && !prepaid && remaining > 0 && hasPermission("payments.create") && (
            <button onClick={() => setSettle(true)} data-testid="settlement-receipt-button"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-ink-line dark:text-ink-text dark:hover:bg-ink-panel">
              Record Settlement Receipt
            </button>
          )}
        </div>
      </div>

      {settle && (
        <SettlementModal inv={inv} onClose={() => setSettle(false)} onDone={async () => { setSettle(false); await load(); await onChange(); }} />
      )}
    </div>
  );
}

function SettlementModal({ inv, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [ref, setRef] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!amount) { toast.error("Enter the settlement amount"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/finance/payment_receipt", {
        source_document_type: "invoice", source_document_id: inv.id, receipt_kind: "settlement",
        paid_amount: String(amount), currency: inv.currency, payment_reference: ref || undefined,
      });
      await api.post(`/finance/payment_receipt/${data.id}/issue`, {});
      toast.success("Settlement receipt issued");
      await onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="settlement-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900 dark:text-ink-text">Settlement Receipt</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>
        <div className="mb-3 rounded-lg bg-slate-50 p-3 text-sm dark:bg-ink-panel/40">
          <div className="flex justify-between"><span className="text-slate-500">Remaining balance</span><span className="font-mono-tf font-semibold text-slate-800 dark:text-ink-text">{money(inv.currency, inv.remaining_balance)}</span></div>
        </div>
        <div className="space-y-3">
          <div><label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Amount ({inv.currency}) *</label><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="settlement-amount-input" className={INPUT} /></div>
          <div><label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Payment reference</label><input value={ref} onChange={(e) => setRef(e.target.value)} className={INPUT} /></div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 dark:border-ink-line dark:text-slate-300">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="settlement-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-ink-gold px-4 py-2 text-sm font-semibold text-[#0B1120] disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Issue Settlement Receipt
          </button>
        </div>
      </div>
    </div>
  );
}
